import { EditOutlined, PlusOutlined } from "@ant-design/icons"
import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { Button } from "components/Button"
import { Card } from "components/Card/Card"
import PreviousButton from "components/Previous/PreviousButton"
import YesNoToggle from "components/YesNoToggle/YesNoToggle"
import { GenericModal } from "components/Modal/Modal"
import { adminOwnerInfoReset, adminOwnerInfoUpdateRequest } from "containers/Admin/services/actions"
import { cdn } from "core/cdn"
import { useLanguage } from "locales"
import { Message } from "types/redux"
import { AdminStore, ClientStore } from "types/store"

import ownerStyles from "../PharmacyOwnerInfo/PharmacyOwnerInfo.module.scss"
import MssEmailEntry, { MSS_EMAIL_PATTERN } from "./MssEmailEntry"
import styles from "./MssSettings.module.scss"
import { DirectoryMss, knownMssEmail } from "./useDirectoryMss"

export type MssSettingsStep = "main" | "entry"

// MSS preference of the pharmacy: the address its communications are sent to, and whether it
// wants them at all. Rendered inside the "Espace professionnel" modal.
const MssSettings: React.FC<{
  directory: DirectoryMss
  // Open on the address entry right away: a new owner confirms (or enters) the MSS of their RPPS
  confirmNewOwner?: boolean
  // saved: the confirmation to show where the pharmacist goes back to, after a new owner's save
  onBack: (saved?: Message) => void
  // Lets the modal adapt its size to the current screen
  onStepChange?: (step: MssSettingsStep) => void
}> = ({ directory, confirmNewOwner = false, onBack, onStepChange }) => {
  const dispatch = useDispatch()
  const { languages } = useLanguage()
  const admin = useSelector(({ client }: { client: ClientStore }) => client.admin)
  const ownerInfo = useSelector(({ admin }: { admin: Partial<AdminStore> }) => admin.ownerInfo)

  const [step, setStep] = useState<MssSettingsStep>(confirmNewOwner ? "entry" : "main")
  const [notice, setNotice] = useState<Message | null>(null)
  // Warning before refusing the communications: from the NON button, or when a new owner
  // continues without confirming the address found for their RPPS
  const [warning, setWarning] = useState<"optOut" | null>(null)
  // Only a save started from these screens may change them: the status found at mount belongs
  // to a previous save (e.g. the new owner's billing, right before this screen opens)
  const saveStarted = useRef(false)

  useEffect(() => {
    onStepChange?.(step)
  }, [step, onStepChange])

  useEffect(() => {
    dispatch(adminOwnerInfoReset())
    return () => {
      dispatch(adminOwnerInfoReset())
    }
  }, [dispatch])

  useEffect(() => {
    if (!saveStarted.current) return
    if (ownerInfo?.status === "success" || ownerInfo?.status === "error") saveStarted.current = false
    if (ownerInfo?.status === "success") {
      setWarning(null)
      if (confirmNewOwner) {
        // The new owner confirmed their address: straight back to "Informations du titulaire",
        // which shows the confirmation since this screen closes at once
        onBack(ownerInfo.message ?? undefined)
        return
      }
      setStep("main")
      setNotice(ownerInfo.message ?? null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerInfo?.status])

  const loading = !!ownerInfo?.loading
  const saveError = ownerInfo?.status === "error" ? ownerInfo.message : undefined
  const current = knownMssEmail(admin, directory)
  // Without any address (stored or from the directory) nothing can be received: OUI stays grey
  // until one is entered. Otherwise communications are on unless the pharmacist refused them
  // (no choice yet = directory address applies)
  const canReceive = !!current
  const active = canReceive && admin?.mss_opt_in !== false
  // The preference is stored with the owner's RPPS and FINESS: nothing can be saved before the RPPS is set
  const hasOwner = !!admin?.rpps

  const setOptIn = (optIn: boolean) => {
    if (loading) return
    setNotice(null)
    saveStarted.current = true
    // The choice is made about the address shown: store it with the preference, so a directory
    // address not yet saved is kept (unless the API would refuse its domain)
    const addressToStore = current && !admin?.mss_email && MSS_EMAIL_PATTERN.test(current) ? current : undefined
    dispatch(adminOwnerInfoUpdateRequest({ mss_opt_in: optIn, ...(addressToStore ? { mss_email: addressToStore } : {}) }))
  }

  const warningModal = (
    <GenericModal
      visible={warning !== null}
      // Same width as the other popups; the height follows the content (48px title on one line)
      style={{ width: 1550 }}
      titleProps={{ size: "medium", doNotAnimateTitle: true }}
      title={
        <div className={styles.WarningTitle}>
          <img src={cdn("/images/warning_picto.svg")} alt="" width={140} height={140} />
          <span>{languages.ownerInfoMssOptOutTitle}</span>
        </div>
      }
      bodyStyle={{ textAlign: "center", padding: "20px 0 40px" }}
      cancelText={languages.cancel}
      onCancel={() => setWarning(null)}
      onMaskClick={() => setWarning(null)}
      acceptText={languages.ownerInfoMssDeactivate}
      acceptButtonProps={{ loading, disabled: loading }}
      onAccept={() => setOptIn(false)}
    >
      <div>
        <p className={ownerStyles.OptOutText}>{languages.ownerInfoMssOptOutText}</p>
        <p className={ownerStyles.OptOutHint}>{languages.ownerInfoMssOptOutReactivateHint}</p>
      </div>
    </GenericModal>
  )

  if (step === "entry") {
    // The directory answer prefills the field: wait for it so the entry opens with the address
    if (directory.loading) {
      return (
        <>
          <PreviousButton onClick={() => onBack()} />
          <Card />
        </>
      )
    }
    return (
      <>
        <MssEmailEntry
          initialValue={current ?? ""}
          recovered={!admin?.mss_email && !!directory.email}
          confirmUnchanged={confirmNewOwner}
          onSkip={
            confirmNewOwner
              ? (optOut) => {
                  // Skipping a shown address is a refusal of the MSS communications; without any
                  // address there is nothing to store
                  if (optOut) {
                    setOptIn(false)
                    return
                  }
                  dispatch(adminOwnerInfoReset())
                  // The new owner's RPPS and FINESS are saved: confirm it on the way back
                  onBack({ type: "success", text: languages.ownerInfoSaved })
                }
              : undefined
          }
          loading={loading}
          saveError={saveError}
          onBack={() => {
            // Keep the pending state while a save is running, so nothing re-enables meanwhile
            if (!loading) dispatch(adminOwnerInfoReset())
            // A new owner came straight from the billing save: back to "Informations du titulaire",
            // the MSS home screen was never shown to them. Their RPPS and FINESS are saved
            if (confirmNewOwner) onBack({ type: "success", text: languages.ownerInfoSaved })
            else setStep("main")
          }}
          onConfirm={(email) => {
            setNotice(null)
            saveStarted.current = true
            dispatch(adminOwnerInfoUpdateRequest({ mss_email: email }))
          }}
        />
      </>
    )
  }

  return (
    <>
      <PreviousButton onClick={() => onBack()} />
      <Card message={notice ?? saveError ?? undefined}>
        <h1 className={`${ownerStyles.Title} ${styles.TitleWithScope}`}>{languages.mssSettingsTitle}</h1>
        <p className={styles.Scope}>
          {hasOwner ? languages.mssSettingsScope : languages.mssSettingsNeedsRpps}
        </p>
        <div className={ownerStyles.Rows}>
          <div className={ownerStyles.Row}>
            <span className={ownerStyles.Label}>{languages.ownerInfoMssLabel}</span>
            <span className={current ? ownerStyles.Value : `${ownerStyles.Value} ${ownerStyles.Empty}`}>
              {current || languages.not_informed_singular}
            </span>
            <Button
              className={ownerStyles.RowButton}
              icon={current ? <EditOutlined /> : <PlusOutlined />}
              // Wait for the directory answer so the entry opens with the right address
              disabled={!hasOwner || directory.loading}
              onClick={() => {
                dispatch(adminOwnerInfoReset())
                setNotice(null)
                setStep("entry")
              }}
            >
              {(current ? languages.modify : languages.add).toUpperCase()}
            </Button>
          </div>
        </div>
        <div className={styles.ToggleRow}>
          <span className={styles.ToggleLabel}>{languages.mssSettingsToggleLabel}</span>
          <YesNoToggle
            value={hasOwner && canReceive ? active : undefined}
            disabled={loading || !hasOwner || !canReceive}
            onYes={() => setOptIn(true)}
            onNo={() => setWarning("optOut")}
          />
        </div>
      </Card>
      {warningModal}
    </>
  )
}

export default MssSettings
